import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { jwtDecode } from 'jwt-decode';
import { api } from "../pages/api";
import { REFRESH_TOKEN, ACCESS_TOKEN } from "../pages/constants";
import Cookies from 'js-cookie';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [sessionId, setSessionId] = useState(Cookies.get('session_id'));
    const [isInitialMount, setIsInitialMount] = useState(true);

    const updateSession = useCallback((newSessionId) => {
        if (newSessionId && newSessionId !== sessionId) {
            setSessionId(newSessionId);
            // Update the API instance's headers
            api.defaults.headers['X-Session-Id'] = newSessionId;
            // Dispatch session update event
            window.dispatchEvent(new CustomEvent('sessionUpdate', { detail: newSessionId }));
        }
    }, [sessionId]);

    const refreshToken = useCallback(async (signal) => {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN);
        if (!refreshToken) {
            setIsAuthorized(false);
            delete api.defaults.headers['Authorization'];
            return;
        }

        try {
            const res = await api.post("/api/token/refresh/",
                { refresh: refreshToken },
                { signal: signal }
            );

            if (res.status === 200) {
                const newToken = res.data.access;
                localStorage.setItem(ACCESS_TOKEN, newToken);
                api.defaults.headers['Authorization'] = `Bearer ${newToken}`;
                setIsAuthorized(true);
            } else {
                setIsAuthorized(false);
                delete api.defaults.headers['Authorization'];
            }
        } catch (error) {
            if (!signal.aborted) {
                console.error("Error refreshing token:", error);
                setIsAuthorized(false);
                localStorage.removeItem(ACCESS_TOKEN);
                localStorage.removeItem(REFRESH_TOKEN);
                delete api.defaults.headers['Authorization'];
            }
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();

        const checkAuth = async () => {
            const token = localStorage.getItem(ACCESS_TOKEN);
            if (!token) {
                if (isMounted) {
                    setIsAuthorized(false);
                    setIsLoading(false);
                }
                return;
            }

            try {
                const decoded = jwtDecode(token);
                const tokenExpiration = decoded.exp;
                const now = Date.now() / 1000;

                if (tokenExpiration < now) {
                    await refreshToken(abortController.signal);
                } else if (isMounted) {
                    setIsAuthorized(true);
                    // Ensure API has the token
                    api.defaults.headers['Authorization'] = `Bearer ${token}`;
                }
            } catch (error) {
                if (!abortController.signal.aborted) {
                    console.error("Error decoding token:", error);
                    setIsAuthorized(false);
                    localStorage.removeItem(ACCESS_TOKEN);
                    localStorage.removeItem(REFRESH_TOKEN);
                    delete api.defaults.headers['Authorization'];
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        // Only run session check on initial mount
        if (isInitialMount) {
            const currentSessionId = Cookies.get('session_id');
            if (currentSessionId && currentSessionId !== sessionId) {
                updateSession(currentSessionId);
            }
            setIsInitialMount(false);
        }

        checkAuth();

        // Set up cookie change listener
        const cookieListener = (event) => {
            if (event.key === 'session_id' && event.newValue !== sessionId) {
                updateSession(event.newValue);
            }
        };
        window.addEventListener('storage', cookieListener);

        return () => {
            isMounted = false;
            abortController.abort();
            window.removeEventListener('storage', cookieListener);
        };
    }, [sessionId, isInitialMount, refreshToken, updateSession]);

    const login = useCallback(async (accessToken, refreshToken) => {
        localStorage.setItem(ACCESS_TOKEN, accessToken);
        localStorage.setItem(REFRESH_TOKEN, refreshToken);
        api.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
        setIsAuthorized(true);
    }, []);

    const logout = useCallback(async () => {
        try {
            // Call backend logout endpoint which will flush session and create new one
            return await api.post("/api/logout/");
        } catch (error) {
            console.error("Error during logout:", error);
            throw error;
        } finally {
            // Always clear browser auth state so public quote links cannot inherit a prior user.
            localStorage.removeItem(ACCESS_TOKEN);
            localStorage.removeItem(REFRESH_TOKEN);
            delete api.defaults.headers['Authorization'];
            setIsAuthorized(false);
        }
    }, []);

    const contextValue = useMemo(() => ({
        isAuthorized,
        isLoading,
        login,
        logout,
        sessionId,
        updateSession
    }), [isAuthorized, isLoading, login, logout, sessionId, updateSession]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
